import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/**
 * TipTap extension that highlights {AI instruction} blocks with a purple background
 * and {{variable}} blocks with a pale blue background.
 * Uses ProseMirror Decorations (visual-only, not stored in the document model).
 */

const VALID_VARIABLES = new Set([
  'firstName', 'lastName', 'email', 'company', 'linkedinUrl',
  'senderFirstName', 'senderLastName',
]);

const AI_INSTRUCTION_REGEX = /(?<!\{)\{(?!\{)((?:[^{}]|\{\{[^{}]*\}\})*)\}(?!\})/g;
const VARIABLE_REGEX = /\{\{([^{}]*)\}\}/g;

const pluginKey = new PluginKey('aiInstructionHighlight');

function buildDecorations(doc: any): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node: any, pos: number) => {
    if (!node.isText) return;

    const text = node.text!;
    let match: RegExpExecArray | null;
    const varRanges: Array<[number, number]> = [];

    // Match {{variable}} first (pale blue) — only valid variable names
    const varRe = new RegExp(VARIABLE_REGEX.source, VARIABLE_REGEX.flags);
    while ((match = varRe.exec(text)) !== null) {
      const from = pos + match.index;
      const to = from + match[0].length;
      if (VALID_VARIABLES.has(match[1].trim())) {
        varRanges.push([from, to]);
        decorations.push(
          Decoration.inline(from, to, {
            class: 'variable-highlight',
          }),
        );
      }
    }

    // Match {AI instruction} (purple) — skip ranges already covered by {{variable}}
    const aiRe = new RegExp(AI_INSTRUCTION_REGEX.source, AI_INSTRUCTION_REGEX.flags);
    while ((match = aiRe.exec(text)) !== null) {
      const from = pos + match.index;
      const to = from + match[0].length;
      const overlaps = varRanges.some(([vFrom, vTo]) => from >= vFrom && to <= vTo);
      if (!overlaps) {
        decorations.push(
          Decoration.inline(from, to, {
            class: 'ai-instruction-highlight',
          }),
        );
      }
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const AiInstructionHighlight = Extension.create({
  name: 'aiInstructionHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        state: {
          init(_, { doc }) {
            return buildDecorations(doc);
          },
          apply(tr, oldSet) {
            if (!tr.docChanged) return oldSet;
            return buildDecorations(tr.doc);
          },
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
